// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  clearObstacleSelection,
  selectObstacle
} from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';

const legacySelectionHandlers = new Map();
const legacySelectionClearHandlers = new Map();
const legacySelectionCollectors = new Map();

function normalizedOwner(owner) {
  return String(owner || '').trim();
}

function deepReadonlyClone(value, seen = new WeakMap()) {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  let descriptors;
  try {
    descriptors = Object.getOwnPropertyDescriptors(value);
  } catch (_error) {
    return Object.freeze(clone);
  }
  for (const [key, descriptor] of Object.entries(descriptors)) {
    if (!descriptor.enumerable || !Object.hasOwn(descriptor, 'value')) continue;
    try {
      Object.defineProperty(clone, key, {
        configurable: false,
        enumerable: true,
        value: deepReadonlyClone(descriptor.value, seen),
        writable: false
      });
    } catch (_error) {
      // Nicht klonbare Werte duerfen weder Selection-Lesen noch Collector
      // anderer Systeme abbrechen. Der betreffende Wert wird ausgelassen.
    }
  }
  return Object.freeze(clone);
}

function reportLegacySelectionFailure(owner, phase, error) {
  try {
    console.warn(`[selection] Legacy-${phase} von ${owner || '(unbekannt)'} ist fehlgeschlagen.`, error);
  } catch (_reportingError) {
    // Ein defekter Reporter darf die Selection-Fehlerisolation nicht aufheben.
  }
}

function isolateUnexpectedThenable(owner, phase, value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
  let then;
  try {
    then = value.then;
  } catch (error) {
    reportLegacySelectionFailure(owner, `${phase}/Thenable`, error);
    return true;
  }
  if (typeof then !== 'function') return false;
  try {
    Promise.resolve(value).then(
      () => undefined,
      error => reportLegacySelectionFailure(owner, `${phase}/Thenable`, error)
    );
  } catch (error) {
    reportLegacySelectionFailure(owner, `${phase}/Thenable`, error);
  }
  return true;
}

/**
 * Stellt sicher ensure State im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function ensureState() {
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!Array.isArray(S.selection.items)) S.selection.items = [];
  return S.selection;
}

/**
 * Normalisiert normalize Item im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} item - Eingabewert für normalize Item.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeItem(item) {
  try {
    if (!item || !item.owner || !item.kind || !item.id) return null;
    const owner = normalizedOwner(item.owner);
    const kind = String(item.kind || '').trim();
    const id = String(item.id || '').trim();
    if (!owner || !kind || !id) return null;
    return Object.freeze({
      owner,
      kind,
      id,
      meta: item.meta && typeof item.meta === 'object'
        ? deepReadonlyClone(item.meta)
        : Object.freeze({})
    });
  } catch (_error) {
    return null;
  }
}

function readonlyItems(rawItems = []) {
  const source = Array.isArray(rawItems) ? rawItems : [];
  const result = [];
  let length;
  try {
    length = source.length;
  } catch (_error) {
    return Object.freeze(result);
  }
  for (let index = 0; index < length; index += 1) {
    try {
      const normalized = normalizeItem(source[index]);
      if (normalized) result.push(normalized);
    } catch (_error) {
      // Ein fehlerhafter Eintrag darf weitere Selection-Items nicht verdecken.
    }
  }
  return Object.freeze(result);
}

/**
 * Beschreibt items im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function items() {
  return readonlyItems(ensureState()?.items || []);
}

/**
 * Beschreibt primary im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function primary() {
  return items()[0] || null;
}

/**
 * Entfernt clear im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Entfernen oder Zurücksetzen von State, DOM oder Auswahl, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function clear(options = {}) {
  const selection = ensureState();
  if (!selection) return [];
  selection.items = [];
  if (options.legacy !== false) clearLegacySelection();
  return items();
}

/**
 * Setzt select im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} item - Eingabewert für select.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function select(item, options = {}) {
  const selection = ensureState();
  const normalized = normalizeItem(item);
  if (!selection || !normalized) return null;
  if (options.append) {
    const exists = selection.items.some(entry => (
      entry.owner === normalized.owner &&
      entry.kind === normalized.kind &&
      entry.id === normalized.id
    ));
    if (!exists) selection.items.push(normalized);
  } else {
    selection.items = [normalized];
  }
  if (options.legacy !== false) syncLegacySelection(normalized);
  return normalized;
}

/**
 * Beschreibt replace From Legacy im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} rawItems - Eingabewert für replace From Legacy.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function replaceFromLegacy(rawItems = []) {
  const selection = ensureState();
  if (!selection) return [];
  selection.items = [...readonlyItems(rawItems)];
  return items();
}

/**
 * Beschreibt sync From Legacy im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function syncFromLegacy() {
  if (!S.selection) return [];
  const legacy = S.selection;
  const next = [];

  for (const collector of orderedLegacySelectionCollectors()) {
    const snapshot = makeLegacyCollectorContext(legacy, collector.owner);
    next.push(...collectLegacyItems(collector, 'collectMulti', snapshot.legacy, snapshot.context));
  }

  const obstacleIds = Array.isArray(legacy.obstacleIds) && legacy.obstacleIds.length
    ? legacy.obstacleIds
    : legacy.obstacleId
      ? [legacy.obstacleId]
      : [];
  if (obstacleIds.length) {
    for (const id of obstacleIds) next.push({ owner: 'core', kind: 'obstacle', id });
  } else {
    for (const collector of orderedLegacySelectionCollectors()) {
      const snapshot = makeLegacyCollectorContext(legacy, collector.owner);
      const primaryItems = collectLegacyItems(collector, 'collectPrimary', snapshot.legacy, snapshot.context);
      if (primaryItems.length) {
        next.push(...primaryItems);
        break;
      }
    }
  }

  return replaceFromLegacy(dedupeItems(next));
}

/**
 * Prüft has im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} owner - Eingabewert für has.
 * @param {*} kind - Eingabewert für has.
 * @param {*} id - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function has(owner, kind, id = null) {
  return items().some(item => (
    item.owner === owner &&
    item.kind === kind &&
    (id === null || item.id === id)
  ));
}

export function registerLegacySelectionHandler(owner, handler) {
  const ownerId = normalizedOwner(owner);
  if (!ownerId || typeof handler !== 'function') return false;
  if (legacySelectionHandlers.has(ownerId)) return false;
  legacySelectionHandlers.set(ownerId, handler);
  return true;
}

export function unregisterLegacySelectionHandler(owner, handler) {
  const ownerId = normalizedOwner(owner);
  if (!ownerId || typeof handler !== 'function') return false;
  if (legacySelectionHandlers.get(ownerId) !== handler) return false;
  return legacySelectionHandlers.delete(ownerId);
}

export function registerLegacySelectionClearHandler(owner, handler) {
  const ownerId = normalizedOwner(owner);
  if (!ownerId || typeof handler !== 'function') return false;
  if (legacySelectionClearHandlers.has(ownerId)) return false;
  legacySelectionClearHandlers.set(ownerId, handler);
  return true;
}

export function unregisterLegacySelectionClearHandler(owner, handler) {
  const ownerId = normalizedOwner(owner);
  if (!ownerId || typeof handler !== 'function') return false;
  if (legacySelectionClearHandlers.get(ownerId) !== handler) return false;
  return legacySelectionClearHandlers.delete(ownerId);
}

export function registerLegacySelectionCollector(owner, collector) {
  if (!owner || !collector) return false;
  const ownerId = normalizedOwner(owner);
  if (!ownerId) return false;
  if (legacySelectionCollectors.has(ownerId)) return false;
  const entry = typeof collector === 'function'
    ? { owner: ownerId, collectPrimary: collector }
    : { ...collector, owner: ownerId };
  if (typeof entry.collectMulti !== 'function' && typeof entry.collectPrimary !== 'function') return false;
  const numericPriority = Number(entry.priority);
  entry.priority = Number.isFinite(numericPriority) ? numericPriority : 1000;
  entry.registration = collector;
  legacySelectionCollectors.set(ownerId, entry);
  return true;
}

export function unregisterLegacySelectionCollector(owner, collector) {
  const ownerId = normalizedOwner(owner);
  if (!ownerId || !collector) return false;
  if (legacySelectionCollectors.get(ownerId)?.registration !== collector) return false;
  return legacySelectionCollectors.delete(ownerId);
}

/**
 * Entfernt clear Legacy Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Aktualisiert die globale Auswahl und synchronisiert nachgelagerte UI-Zustände.
 */
function clearLegacySelection() {
  SystemRegistry.clearAllSelections();
  clearObstacleSelection();
  AppRuntime.clearMultiSelection();
}

export function clearLegacySelectionForOwner(owner) {
  const selection = ensureState();
  if (!selection) return [];
  const ownerId = String(owner || '').trim();
  const registeredHandler = legacySelectionClearHandlers.get(ownerId);
  if (typeof registeredHandler === 'function') {
    try {
      const result = registeredHandler(ownerId);
      if (isolateUnexpectedThenable(ownerId, 'Clear-Handler', result)) return syncFromLegacy();
      if (result === true) return syncFromLegacy();
    } catch (error) {
      reportLegacySelectionFailure(ownerId, 'Clear-Handler', error);
    }
  }

  if (ownerId === 'core') {
    selection.obstacleId = null;
    selection.obstacleIds = [];
  }

  return syncFromLegacy();
}

export function clearSystemSelectionsExcept(systemId) {
  return SystemRegistry.clearSelectionsExcept(systemId);
}

/**
 * Beschreibt sync Legacy Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} item - Eingabewert für sync Legacy Selection.
 * Seiteneffekte: Aktualisiert die globale Auswahl und synchronisiert nachgelagerte UI-Zustände.
 */
function syncLegacySelection(item) {
  if (!item) return;
  const registeredHandler = legacySelectionHandlers.get(item.owner);
  if (typeof registeredHandler === 'function') {
    try {
      const result = registeredHandler(deepReadonlyClone(item));
      if (isolateUnexpectedThenable(item.owner, 'Handler', result)) return;
      if (result === true) return;
    } catch (error) {
      reportLegacySelectionFailure(item.owner, 'Handler', error);
    }
  }
  if (item.owner === 'core' && item.kind === 'obstacle') {
    selectObstacle(item.id);
  }
}

/**
 * Beschreibt dedupe Items im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} rawItems - Eingabewert für dedupe Items.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function dedupeItems(rawItems = []) {
  const seen = new Set();
  const next = [];
  for (const item of rawItems) {
    const normalized = normalizeItem(item);
    if (!normalized) continue;
    const key = `${normalized.owner}:${normalized.kind}:${normalized.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(normalized);
  }
  return next;
}

function orderedLegacySelectionCollectors() {
  return [...legacySelectionCollectors.values()]
    .sort((a, b) => (a.priority || 0) - (b.priority || 0));
}

function makeLegacyCollectorContext(legacy, owner) {
  const legacySnapshot = deepReadonlyClone(legacy || {});
  let rawItems;
  try {
    rawItems = legacy?.items || [];
  } catch (_error) {
    rawItems = [];
  }
  const ownerItems = readonlyItems(rawItems)
    .filter(item => item.owner === owner);
  const context = Object.freeze({
    legacy: legacySnapshot,
    items: Object.freeze(ownerItems),
    multiSelection: legacySnapshot?.multiSelection && typeof legacySnapshot.multiSelection === 'object'
      ? legacySnapshot.multiSelection
      : null
  });
  return { legacy: legacySnapshot, context };
}

function collectLegacyItems(collector, method, legacy, context) {
  const collect = collector?.[method];
  if (typeof collect !== 'function') return [];
  let result;
  try {
    result = collect(legacy, context);
  } catch (error) {
    reportLegacySelectionFailure(collector?.owner, `Collector/${method}`, error);
    return [];
  }
  if (isolateUnexpectedThenable(collector?.owner, `Collector/${method}`, result)) return [];
  if (!result) return [];

  let candidates;
  try {
    candidates = Array.isArray(result) ? result : [result];
  } catch (error) {
    reportLegacySelectionFailure(collector?.owner, `Collector/${method}`, error);
    return [];
  }
  const collected = [];
  let length;
  try {
    length = candidates.length;
  } catch (error) {
    reportLegacySelectionFailure(collector?.owner, `Collector/${method}`, error);
    return [];
  }
  for (let index = 0; index < length; index += 1) {
    try {
      const candidate = candidates[index];
      const normalized = normalizeItem({
        owner: collector.owner,
        kind: candidate?.kind,
        id: candidate?.id,
        meta: candidate?.meta
      });
      if (normalized) collected.push(normalized);
    } catch (error) {
      reportLegacySelectionFailure(collector?.owner, `Collector/${method}/Eintrag`, error);
    }
  }
  return collected;
}
